import type { NewUserHistory } from '@/server/db/schema';
import { isAssistantChatEvent, isSuggestionEvent, isUserChatEvent } from '@/lib/chatEvents';

const rowId = (row: NewUserHistory) => row.id ?? 0;

// The server saves 'user chat' as 'user chat message' (channel suffixes too), so match by kind.
const turnKind = (row: NewUserHistory) => {
	const event = row.event ?? '';
	if (isSuggestionEvent(event)) return 'draft';
	if (isUserChatEvent(event)) return 'user';
	if (isAssistantChatEvent(event)) return 'agent';
	return event;
};
const sameTurn = (a: NewUserHistory, b: NewUserHistory) =>
	turnKind(a) === turnKind(b) && (a.message ?? '').trim() === (b.message ?? '').trim();

/**
 * The fetched page can be older than the screen: sends add unsaved rows (id 0) and saved rows while
 * it is in flight. Dropping those until the next reload makes messages flash, so they are kept;
 * a row that was on screen at fetch start but missing from the page was deleted.
 */
export function mergeHistoryPage({
	existing,
	page,
	idsAtFetchStart,
}: {
	existing: NewUserHistory[];
	page: NewUserHistory[];
	idsAtFetchStart: Set<number>;
}): { merged: NewUserHistory[]; keptOlder: boolean } {
	const pageIds = new Set(page.map(rowId));
	const oldestPageId = page[0] ? rowId(page[0]) : undefined;
	const older = oldestPageId
		? existing.filter((row) => rowId(row) > 0 && rowId(row) < oldestPageId)
		: [];

	// Each saved row in the page that the screen did not have can stand in for one unsaved row.
	const unclaimed = page.filter((row) => !existing.some((e) => rowId(e) === rowId(row)));
	const pending = existing.filter((row) => {
		const id = rowId(row);
		if (id > 0) return !pageIds.has(id) && !idsAtFetchStart.has(id) && (!oldestPageId || id > oldestPageId);
		const match = unclaimed.findIndex((saved) => sameTurn(saved, row));
		if (match === -1) return true;
		unclaimed.splice(match, 1);
		return false;
	});

	return { merged: [...older, ...page, ...pending], keptOlder: older.length > 0 };
}
